import { CheckIcon } from '@heroicons/react/outline';
import appI18nInstance from 'i18n';
import type { RouteLockType } from 'types/routing.types';
import {
  type LockContentType,
  SubscriptionLockKeysEnum,
} from 'types/subscriptions.types';
import {
  getNormalizedTaxSlugForTranslations,
  TaxTypeToLockMap,
} from './helpers';
import { WEBAPP_BANKING_SUBACCOUNTS_VIEWING_PAYWALL } from 'utils/amplitude/events/bank.amplitude';
import homeBlurredBG from 'assets/images/paywalls/home_bg.webp';
import banksBlurredBG from 'assets/images/paywalls/banks_bg.webp';
import revenuesBlurredBG from 'assets/images/paywalls/revenues_bg.webp';
import expensesBlurredBG from 'assets/images/paywalls/expenses_bg.webp';
import taxesBlurredBG from 'assets/images/paywalls/taxes_bg.webp';
import advisorBlurredBG from 'assets/images/paywalls/advisor_bg.webp';
import exportBlurredBG from 'assets/images/paywalls/export_bg.webp';
import docsBlurredBG from 'assets/images/paywalls/docs_bg.webp';
import expensesAnalyticsBlurredBG from 'assets/images/paywalls/expenses_analytics_bg.webp';
import revenuesAnalyticsBlurredBG from 'assets/images/paywalls/revenues_analytics_bg.webp';
import taxesAnalyticsBlurredBG from 'assets/images/paywalls/taxes_analytics_bg.webp';
import amortizationBlurredBG from 'assets/images/paywalls/amortisation_bg.webp';

const homePageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebHomePage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.home_page.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.home_page.description'),
  backgroundImage: homeBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const bankingPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebBankingPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.bank_page.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.bank_page.description'),
  backgroundImage: banksBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const expensesPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebExpensesPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.expenses_page.title', {
      recommendedPlanName,
    }),
  backgroundImage: expensesBlurredBG,
  description: () =>
    appI18nInstance.t('feature_lock_paywall.expenses_page.description'),
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const revenuesPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebRevenuesPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.revenues_page.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.revenues_page.description'),
  backgroundImage: revenuesBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const amortizationPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.createAmortizationExpense,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.amortisation.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.amortisation.description'),
  backgroundImage: amortizationBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const createAmortizationExpenseLock: LockContentType = {
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.amortisation.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.amortisation.description'),
  backgroundImage: amortizationBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const taxesPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebTaxPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.taxes_page.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.taxes_page.description'),
  backgroundImage: taxesBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const aiAdvisorPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebAIAdvisorPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.tax_advisor.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.tax_advisor.description'),
  backgroundImage: advisorBlurredBG,
  primaryFeature: (features) => ({
    title: appI18nInstance.t('plan_feature.tax_advisor_access'),
    description:
      features?.counters?.taxAdvisorMonthlyMessages?.total === 'INFINITE'
        ? appI18nInstance.t('plan_features.tax_advisor_unlimited')
        : appI18nInstance.t('plan_features.tax_advisor', {
            numberofQuestions:
              features?.counters?.taxAdvisorMonthlyMessages?.total,
          }),
    Icon: CheckIcon,
  }),
};

const exportPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebExportPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.data_export.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.data_export.description'),
  backgroundImage: exportBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const expensesAnalyticsPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebExpensesAnalyticsPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.expenses_analytics.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.expenses_analytics.description'),
  backgroundImage: expensesAnalyticsBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const revenuesAnalyticsPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebRevenuesAnalyticsPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.revenues_analytics.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.revenues_analytics.description'),
  backgroundImage: revenuesAnalyticsBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const repositoryPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebDocRepositoryPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.doc_repo.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.doc_repo.description'),
  backgroundImage: docsBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const taxesAnalyticsPageLock: RouteLockType = {
  lockKey: SubscriptionLockKeysEnum.viewWebTaxesAnalyticsPage,
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.taxes_analytics.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.taxes_analytics.description'),
  backgroundImage: taxesAnalyticsBlurredBG,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.complete_web'),
    description: appI18nInstance.t('plan_features.complete_web.description'),
    Icon: CheckIcon,
  }),
};

const taxPageLock: RouteLockType = ({ params }) => {
  const { country, taxSlug } = params;

  const lockKey =
    TaxTypeToLockMap[`${country}/${taxSlug}` as keyof typeof TaxTypeToLockMap];

  return {
    lockKey,
    title: (recommendedPlanName: string) =>
      appI18nInstance.t(
        `feature_lock_paywall.taxes.${getNormalizedTaxSlugForTranslations(
          taxSlug as string,
        )}.title`,
        {
          recommendedPlanName,
        },
      ),
    description: () =>
      appI18nInstance.t('feature_lock_paywall.tax_submission.description'),
    backgroundImage: taxesBlurredBG,
    primaryFeature: () => ({
      title: appI18nInstance.t('plan_features.all_taxes'),
      description: appI18nInstance.t('plan_features.all_taxes.description'),
      Icon: CheckIcon,
    }),
  };
};

const aiAdvisorMonthlyLimitLockContent: LockContentType = {
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.ai_advisor_monthly_convos.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t(
      'feature_lock_paywall.ai_advisor_monthly_convos.description',
    ),
  primaryFeature: (features) => ({
    title: appI18nInstance.t('plan_feature.tax_advisor_access'),
    description:
      features?.counters?.taxAdvisorMonthlyMessages?.total === 'INFINITE'
        ? appI18nInstance.t('plan_features.tax_advisor_unlimited')
        : appI18nInstance.t('plan_features.tax_advisor', {
            numberofQuestions:
              features?.counters?.taxAdvisorMonthlyMessages?.total,
          }),
    Icon: CheckIcon,
  }),
};

const bankingSpacesLimitLockContent: LockContentType = {
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.banking_spaces.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.banking_spaces.description'),
  eventName: WEBAPP_BANKING_SUBACCOUNTS_VIEWING_PAYWALL,
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.banking_spaces'),
    description: appI18nInstance.t('plan_features.banking_spaces.description'),
    Icon: CheckIcon,
  }),
};

const talkToTaxCoachLockContent: LockContentType = {
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.tax_coaches.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.tax_coaches.description'),
  primaryFeature: () => ({
    title: appI18nInstance.t('plan_features.one_one_tax_coaches'),
    description: appI18nInstance.t(
      'plan_features.one_one_tax_coaches.description',
    ),
    Icon: CheckIcon,
  }),
};

const connectBankAccountsLockContent: LockContentType = {
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.connect_bank_accounts.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.connect_bank_accounts.description'),
  primaryFeature: (features) => ({
    title:
      features?.counters?.bankAccounts?.total === 'INFINITE'
        ? appI18nInstance.t('plan_features.unlimited_banks')
        : appI18nInstance.t('plan_features.bank_connections', {
            numberOfAccounts: features?.counters?.bankAccounts?.total as number,
          }),
    description: appI18nInstance.t(
      'plan_features.bank_connections.description',
    ),
    Icon: CheckIcon,
  }),
};

const expensesMonthlyLimitLockContent: LockContentType = {
  title: (recommendedPlanName: string) =>
    appI18nInstance.t('feature_lock_paywall.monthly_expenses.title', {
      recommendedPlanName,
    }),
  description: () =>
    appI18nInstance.t('feature_lock_paywall.monthly_expenses.description'),
};

export {
  homePageLock,
  bankingPageLock,
  expensesPageLock,
  revenuesPageLock,
  taxesPageLock,
  aiAdvisorPageLock,
  exportPageLock,
  expensesAnalyticsPageLock,
  revenuesAnalyticsPageLock,
  repositoryPageLock,
  taxesAnalyticsPageLock,
  taxPageLock,
  aiAdvisorMonthlyLimitLockContent,
  bankingSpacesLimitLockContent,
  talkToTaxCoachLockContent,
  connectBankAccountsLockContent,
  amortizationPageLock,
  expensesMonthlyLimitLockContent,
  createAmortizationExpenseLock,
};
